<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateForm from 'dashboard/components-next/whatsapp/WhatsAppTemplateForm.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const formRef = ref(null);
const isSyncing = ref(false);
const deletingName = ref(null);

const templates = computed(() => {
  return store.getters['inboxes/getWhatsAppTemplates'](props.inbox.id) || [];
});

const lastSyncedAt = computed(() => {
  return props.inbox.message_templates_last_updated || null;
});

const statusBadgeClass = status => {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED') return 'bg-n-teal-3 text-n-teal-11 border-n-teal-7';
  if (s === 'PENDING' || s === 'IN_APPEAL' || s === 'PENDING_DELETION')
    return 'bg-n-amber-3 text-n-amber-11 border-n-amber-7';
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED')
    return 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-7';
  return 'bg-n-alpha-3 text-n-slate-11 border-n-weak';
};

const bodyText = template => {
  const body = (template.components || []).find(c => c.type === 'BODY');
  return body?.text || '';
};

const handleSync = async () => {
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSyncing.value = false;
  }
};

const openForm = () => formRef.value?.open();

const handleCreated = () => {
  // Sync is triggered backend-side after creation; refetch inbox after a short delay
  setTimeout(() => {
    store.dispatch('inboxes/get');
  }, 2500);
};

const handleDelete = async template => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM', { name: template.name })
    )
  ) {
    return;
  }
  deletingName.value = template.name;
  try {
    await store.dispatch('inboxes/deleteWhatsAppTemplate', {
      inboxId: props.inbox.id,
      templateName: template.name,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_SUCCESS'));
    setTimeout(() => store.dispatch('inboxes/get'), 1500);
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_ERROR');
    useAlert(msg);
  } finally {
    deletingName.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.SUBTITLE') }}
          <span v-if="lastSyncedAt" class="text-xs text-n-slate-10">
            {{
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.LAST_SYNCED', {
                date: new Date(lastSyncedAt).toLocaleString(),
              })
            }}
          </span>
        </p>
      </div>
      <div class="flex gap-2">
        <NextButton
          variant="faded"
          color="slate"
          :is-loading="isSyncing"
          :disabled="isSyncing"
          @click="handleSync"
        >
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC') }}
        </NextButton>
        <NextButton @click="openForm">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW') }}
        </NextButton>
      </div>
    </div>

    <div
      v-if="!templates.length"
      class="flex items-center justify-center p-8 rounded-lg bg-n-alpha-3 border border-n-weak"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
      </p>
    </div>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="template in templates"
        :key="`${template.name}-${template.language}`"
        class="flex items-start justify-between gap-4 p-4 rounded-lg bg-n-alpha-3 border border-n-weak"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ template.name }}
            </span>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-md border"
              :class="statusBadgeClass(template.status)"
            >
              {{ template.status }}
            </span>
            <span
              class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-weak"
            >
              {{ template.category }}
            </span>
            <span
              class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 border border-n-weak"
            >
              {{ template.language }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11 whitespace-pre-wrap break-words">
            {{ bodyText(template) }}
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 px-3 py-1 text-xs rounded-md text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
          :disabled="deletingName === template.name"
          @click="handleDelete(template)"
        >
          {{
            deletingName === template.name
              ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETING')
              : t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')
          }}
        </button>
      </div>
    </div>

    <WhatsAppTemplateForm
      ref="formRef"
      :inbox-id="inbox.id"
      @created="handleCreated"
    />
  </div>
</template>
